'use client';

import { toast } from 'sonner';
import { MARK_ONBOARDING_HOUSEHOLD_COMPLETE_MUTATION } from 'src/features/advisor-book-onboarding/api/mark-complete-gql';
import { useConfirmationDialog } from 'src/hooks/use-confirmation-dialog';
import { handleMutationError } from 'src/utils/handle-mutation-error';

import { useMutationWithErrorLogging } from '@savvy/graphql-hooks/useMutationWithErrorLogging';

type MarkCompleteTarget = {
  readonly id: string;
  readonly householdName: string;
};

type Params = {
  readonly onSuccess?: () => void | Promise<void>;
};

export function useMarkOnboardingHouseholdComplete({ onSuccess }: Params = {}) {
  const [markComplete] = useMutationWithErrorLogging(MARK_ONBOARDING_HOUSEHOLD_COMPLETE_MUTATION);
  const showConfirmation = useConfirmationDialog();

  async function markCompleteForRow(row: MarkCompleteTarget) {
    let succeeded = false;
    try {
      const { data } = await markComplete({
        variables: { stagedHouseholdId: row.id },
      });
      const payload = data?.markOnboardingHouseholdComplete;
      if (payload?.success === true) {
        toast.success(`${row.householdName} has been marked as complete.`);
        succeeded = true;
      } else {
        toast.error(payload?.errors?.[0]?.message ?? 'Could not mark household as complete. Please try again.');
      }
    } catch (err) {
      handleMutationError(err, 'Could not mark household as complete. Please try again.');
    }

    if (succeeded) {
      await onSuccess?.();
      return;
    }

    // Reject so useConfirmationDialog keeps the dialog open for retry.
    throw new Error('Mark onboarding household complete failed');
  }

  function confirmMarkComplete(row: MarkCompleteTarget) {
    showConfirmation({
      title: 'Mark household as complete?',
      description: `This will mark ${row.householdName} as complete. Only confirm once all account transfers have finished. This cannot be undone.`,
      confirmationLabel: 'Mark as complete',
      cancelLabel: 'Cancel',
      onConfirmation: () => markCompleteForRow(row),
    });
  }

  return { confirmMarkComplete };
}
